import { OnlineStorage, DatasetType, PicturePack, PicsConfig, FileDataset, FilePack } from "@tblabs/storage";
import { RefString, TextInput, Button, Snack, MultilineInput, Collection, CollectionPrinter, Div, JsonViewer, RefBool, Checkbox } from "@tblabs/truffle";
import { v4 as uuidv4 } from 'uuid';
import { Bold } from "../Components/Bold";
import { JsonEditor } from "../Components/JsonEditor";
import { Panel } from "../Components/Panel";
import { PrimaryButton } from "../Components/PrimaryButton";
import { Row } from "../Components/Row";
import { FilesUploader } from "./FilesUploader";
import { Page } from "./Page";
import { PicsUploader } from "./PicsUploader";


export class AddItemPage extends Page
{
    constructor(private _storage: OnlineStorage, private datasetName: string)
    {
        super(`Item creation`);

        this.Content(async (body) =>
        {
            const metadata = await this._storage.GetDatasetMetadata(this.datasetName);

            switch (metadata.Type)
            {
                case DatasetType.Object:
                    this.ObjectItemCreationRoutine();
                    break;
                case DatasetType.Text:
                    this.TextItemCreationRoutine();
                    break;
                case DatasetType.Picture:
                    this.PictureItemCreationRoutine();
                    break;
                case DatasetType.File:
                    this.FileItemCreationRoutine();
                    break;
                default:
                    throw new Error("Invalid dataset type. Can not create item.");
            }
        });
    }

    private ObjectItemCreationRoutine()
    {
        const itemId = new RefString(uuidv4().toUpperCase());
        const itemAsString = new RefString("{ }");

        const editor = new JsonEditor(itemAsString);

        this.body.Append(
            new Panel([new Bold(DatasetType.Object), " item creation in ", new Bold(this.datasetName), " dataset"], [
                new Row(
                    new TextInput(itemId).Placeholder("Item id (guid recommended)"),
                    new Button("guid").OnClick(() => itemId.value = uuidv4().toUpperCase())
                ),
            ]),
            new Panel("Object", [
                editor,
                new PrimaryButton("Save").MarginTop(8)
                    .Enable(editor.IsValid)
                    .OnClick(async () =>
                    {
                        const dataset = this._storage.GetObjectDataset(this.datasetName);
                        const item = JSON.parse(itemAsString.value);
                        console.log(item);
                        await dataset.Add(itemId.value, item);
                        new Snack("Changes saved");
                    }),
            ])
        );
    }

    private TextItemCreationRoutine()
    {
        const itemId = new RefString(uuidv4().toUpperCase());
        const itemAsString = new RefString("");

        this.body.Append(
            new Panel([new Bold(DatasetType.Text), " item creation in ", new Bold(this.datasetName), " dataset"], [
                new Row(
                    new TextInput(itemId).Placeholder("Item id (guid recommended)"),
                    new Button("guid").OnClick(() => itemId.value = uuidv4().toUpperCase())
                ),
            ]),
            new Panel("Text", [
                new MultilineInput(itemAsString).Rows(16),
                new PrimaryButton("Save").MarginTop(8)
                    .OnClick(async () =>
                    {
                        const dataset = this._storage.GetTextDataset(this.datasetName);
                        await dataset.AppendText(itemId.value, itemAsString.value);
                        new Snack("Changes saved");
                    }),
            ])
        );
    }

    private PictureItemCreationRoutine()
    {
        const pictures = new Collection<PicturePack>();
        const picConfig: PicsConfig = {
            KeepSource: false,
            Sizes: {
                "thumbnail": { ResizeToWidth: 256 },
                "tiny": { ResizeToHeight: 32 },
            }
        };
        const configAsString = new RefString(JSON.stringify(picConfig, undefined, 2));
        const dataset = this._storage.GetPictureDataset(this.datasetName, picConfig);

        configAsString.OnChange(v =>
        {
            try
            {
                const config = JSON.parse(v) as PicsConfig;

                dataset.SetConfig(config);
            }
            catch (error)
            {
                console.error(error);
            }
        });

        this.body.Append(
            new Panel([new Bold(DatasetType.Picture), " item creation in ", new Bold(this.datasetName), " dataset"], [
                "Config:\n",
                new JsonEditor(configAsString, 8)
            ]),
            new Panel("Pictures", [
                new PicsUploader(dataset).MarginTop(8)
                    .OnSuccessfulUpload((pics: PicturePack[]) => pictures.Load(...pics)),
                new CollectionPrinter(pictures, (picture) => new Div().MarginTop(8).Append(
                    new JsonViewer(picture, "Picture " + picture.Name, { foldAll: false }).Background("#f2f2f2").Padding(8).FontSize(12).BorderRadius(4)
                )),
            ])
        );
    }

    private FileItemCreationRoutine()
    {
        const dataset: FileDataset = this._storage.GetFileDataset(this.datasetName);

        const filesCollection = new Collection<FilePack>();
        const useTags = new RefBool(false).Storable("add-tags-to-files");
        const filesTags = new RefString("").Storable("files-to-add-tags");

        useTags.OnChange(v =>
        {
            if (v == false)
                filesTags.value = "";
        });

        this.body.Append(
            new Panel([new Bold(DatasetType.File), " item creation in ", new Bold(this.datasetName), " dataset"], []),
            new Panel("Files", [
                new Checkbox(useTags, "Add tags to files"),
                new JsonEditor(filesTags).Rows(12).Placeholder(`Files Tags - array of objects, must be equal number of files to upload\n\nExample: [{"foo": "bar"}, {"foo": "qux"}]`).MarginTop(8)
                    .Visible(useTags),
                new FilesUploader(dataset, filesTags).MarginTop(8)
                    .OnSuccessfulUpload((files: FilePack[]) => filesCollection.Load(...files)),
                new CollectionPrinter(filesCollection, (file) => new Div().MarginTop(8).Append(
                    new JsonViewer(file, "File " + file.Name, { foldAll: false }).Background("#f2f2f2").Padding(8).FontSize(12).BorderRadius(4)
                )),
            ])
        );
    }
}
